<!-- TODO: Check if we should run fuzzy search on the server side instead or additionaly to the client side fuzzy search -->

<!-- TODO: Before submitting the selection, display a quick highlight of the selected option (as otherwise the user has no feedback that the selection was successful) -->

<script>
  import uFuzzy from "@leeoniya/ufuzzy"; // 4K gzipped. Fastest, most lightweight and memory efficient fuzzy search library (https://github.com/leeoniya/uFuzzy)
  import { nanoid } from "#lib/util.js";
  // import ScrollIndicator from "#lib/ScrollIndicator.svelte";
  import Popover from "#lib/Popover.svelte";
  import ButtonLabel from "#lib/ButtonLabel.svelte";
  import Image from "#lib/Image.svelte";
  import Icon from "#lib/Icon.svelte";
  import t from "#lib/i18n.svelte.js";
  import { register_click_outside_element } from "#lib/click_outside.svelte.js";
  import { LANGUAGE_SPECIFIC_CHARACTERS } from "#lib/app_config.js";
  import * as env from "$app/env/public";
  import { fade } from "svelte/transition";

  const LANG = env.PUBLIC_LANG;
  const BORDERLESS_TRIGGER_VARIANTS = ["ghostly", "flush", "linkish", "underlined"];

  // TODO: Ability to pass option groups, e.g. "top hits", "recently used", "type A", "type B", "tools", "quick links", etc.

  /**
   * @typedef {Object} SearchSelectOption
   * @property {string} value
   * @property {string} type - 'string' | 'entry_id' | 'number' | 'url' | 'datetime'
   * @property {string} title
   * @property {string} [description]
   * @property {string} [search_keywords]
   * @property {string} [image]
   * @property {string} [icon]
   * @property {boolean} [disabled]
   */

  let {
    /** @type {string} */
    search_input_id = nanoid(),
    /** @type {Array<SearchSelectOption>} */
    options = [],
    /** @type {Snippet} */
    option_content_snippet = null,
    /** @type {Snippet} optional tools visible on hover */
    option_tools_snippet = null,
    /** @type {'small'|'medium'} */
    option_size = "small",
    /** @type {string|string[]|null} */
    value = $bindable(),
    /** @type {string} */
    name = "select",
    /** @type {('dropdown'|'inline')} */
    container = "dropdown",
    /** @type {boolean} */
    enable_search = true,
    /** @type {string} */
    placeholder = container === "dropdown"
      ? t("select_an_option")
      : t("search"),
    /** @type {boolean} */
    allow_null = true,
    /** @type {boolean} */
    multiple = false,
    /**
     * Callback function triggered when search term changes
     * @type {((search_term: string, callback?: (done: boolean) => void) => Promise<void>|void)|null}
     * @description Called when user types in search box. The optional callback parameter can be used
     * to indicate when the search is complete, enabling loading states and option selection.
     */
    on_search = null,
    /** 
     * Callback function triggered when an option is selected
     * @type {((option: SearchSelectOption & {value: string|string[]|null}, event?: any) => void)|null}
     * @description Called when user selects an option from the dropdown or clears the selection.
     * Receives the option object with updated value property.
     */
    on_select = null,
    /** @type {function} */
    on_blur = null,
    /** @type {boolean} */
    disabled = false,
    /** @type {boolean} */
    open_on_focus = true,
    /** @type {boolean} */
    show_search_icon = true,
    /** @type {Snippet} */
    start_icon,
    /** @type {Snippet} */
    end_icon,
    /** @type {string} */
    search_term = $bindable(""),
    /** @type {number} */
    search_debounce = 60,
    /** @type {number} */
    min_search_term_length = 2,
    /**
     * Visual variant for the dropdown trigger.
     * Borderless surface variants (`ghostly`, `flush`, `linkish`, `underlined`) remove the wrapper outline.
     * @type {string}
     */
    variant = "brand subtle-outlined",
    /** @type { 'narrow' | 'small' | 'medium' | 'full' | number | string} */
    width = "small",
    /** @type {'top'|'top-start'|'top-end'|'top-flush'|'bottom'|'bottom-start'|'bottom-end'|'bottom-flush'|'left'|'left-start'|'left-end'|'right'|'right-start'|'right-end'|'center'} Defines the popover's position relative to its anchor */
    placement = "bottom-flush",
    /** @type {boolean} */
    popover_is_open = $bindable(false),
    /** @type {boolean} */
    required = false,
    /**
     * Error message to display below the select
     * @type {string}
     */
    error = '',
  } = $props();

  // Convert width prop to CSS custom property value
  let width_value = $derived.by(() => {
    const width_map = {
      narrow: "15ch",
      small: "242px",
      medium: "min(440px, calc(100vw - var(--size-4)))",
      full: "100%"
    };

    if (width in width_map) {
      return width_map[width];
    }

    return typeof width === "number" ? `${width}px` : `${width}`;
  });

  let search_input_ref = $state();
  let search_input_is_focused = $state(false);
  let anchor = $state();
  let popover_ref = $state();
  let popover_id = nanoid();
  let option_refs = $state([]);
  let focused_index = $state(-1);
  let dropdown_ref = $state();
  let selecting_in_progress = $state(false);
  let sorted_indices = $state(null); // Store indices from uFuzzy
  let awaiting_search_results = $state(false);
  let show_searching_indicator = $state(false);
  let searching_indicator_timeout;
  let has_borderless_trigger_variant = $derived.by(() => {
    if (typeof variant !== "string" || !variant.trim()) {
      return false;
    }

    const variant_tokens = variant.trim().split(/\s+/);
    return BORDERLESS_TRIGGER_VARIANTS.some((variant_name) => variant_tokens.includes(variant_name));
  });

  // uFuzzy instance and options
  let fuzzy_search_options = {
    intraMode: 1, // allows a single error per word
    alpha: `a-z${LANGUAGE_SPECIFIC_CHARACTERS[LANG] || ""}`, // Combine basic Latin alphabet with language-specific characters
  };
  let fuzzy_search = new uFuzzy(fuzzy_search_options);

  // Create the searchable haystack from options (title, description, search_keywords)
  let haystack = $derived(
    options.map((o) => `${o.title} ${o.description || ""} ${o.search_keywords || ""}`)
  );

  // Debounced search handler
  let debounce_timeout;
  function run_internal_fuzzy_search(term) {
    if (!term || term.length < min_search_term_length) {
      sorted_indices = null; // Clear results if term is too short or empty
      return;
    }

    // Ensure haystack is ready
    if (!haystack) {
      sorted_indices = [];
      return;
    }

    // Perform fuzzy search
    let indices = fuzzy_search.filter(haystack, term);

    // indices can be null when the needle is non-searchable (has no alpha-numeric chars)
    if (indices != null && indices.length > 0) {
      // Always get info and sort for search select
      let info = fuzzy_search.info(indices, haystack, term);
      let order = fuzzy_search.sort(info, haystack, term);

      // Map the sorted order back to the original haystack indices
      // info.idx contains indices potentially reduced from the initial `indices`
      // `order` contains indices relative to `info.idx`
      sorted_indices = order.map((o) => info.idx[o]);
    } else {
      sorted_indices = []; // No matches found or non-searchable term
    }
  }

  function handle_search_input(term) {
    if (debounce_timeout) clearTimeout(debounce_timeout);
    if (searching_indicator_timeout) clearTimeout(searching_indicator_timeout); // Clear indicator timeout on new input
    show_searching_indicator = false; // Hide indicator immediately on new input

    debounce_timeout = setTimeout(() => {
      if (on_search) {
        // Set awaiting state and start indicator timeout *when debounced search starts*
        awaiting_search_results = true;
        searching_indicator_timeout = setTimeout(() => {
            show_searching_indicator = true; // Show indicator only if search takes time
        }, 200);

        // Call external search. Pass a callback that runs the internal search *after* external is done.
        // This callback is called by the external search provider
        on_search(term, (done) => {
          clearTimeout(searching_indicator_timeout); // Clear indicator timeout
          show_searching_indicator = false; // Hide indicator
          awaiting_search_results = false;
          // Run the internal fuzzy search now that options might have been updated by the external search
          run_internal_fuzzy_search(term);
        });
      } else {
        // No external search provided, just run the internal fuzzy search
        // No loading indicator needed for purely internal search as it's expected to be fast
        run_internal_fuzzy_search(term);
      }
    }, search_debounce);
  }

  // Reactive filtered options based on uFuzzy results
  let filtered_options = $derived.by(() => {
    const term_active =
      search_term && search_term.length >= min_search_term_length;

    if (!term_active) {
      // Search term is empty or too short, show all options
      return options;
    } else {
      // Search term is active
      if (sorted_indices && sorted_indices.length > 0) {
        return sorted_indices.map((i) => options[i]).filter(Boolean);
      } else {
        // Search term active, but no results found (sorted_indices is null, undefined, or empty array)
        return [];
      }
    }
  });

  // Handle search term changes
  $effect(() => {
    const term = search_term; // capture the reactive value at the start of an effect

    if (term?.length >= min_search_term_length) {
      handle_search_input(term); // handles both internal and external search coordination
    } else {
      // Clear internal results immediately if term is too short or empty
      if (debounce_timeout) clearTimeout(debounce_timeout); // Clear any pending search
      sorted_indices = null;

      // Optionally, inform the external search provider that the term was cleared/shortened
      if (on_search && term?.length < min_search_term_length) {
        // No need to set awaiting_search_results = true here, clearing should be fast.
        // If the external source needs a loading state for clearing, it should handle it internally.
        clearTimeout(searching_indicator_timeout); // Clear potential indicator timeout
        show_searching_indicator = false; // Ensure indicator is hidden
        on_search(term || "", () => {
          // External source has acknowledged the clear/short term.
          // If awaiting_search_results was true from a previous search, set it false.
          awaiting_search_results = false;
          // No need to run internal search here as term is too short and sorted_indices is already null.
        });
      } else if (!on_search) {
        // If no external search, ensure internal results are cleared by calling run_search
        run_internal_fuzzy_search(term); // Will set sorted_indices to null
      }
    }

    if (container === "dropdown" && !popover_is_open && !disabled && term) {
      popover_ref.showPopover();
    }
  });

  // Selected option tracking
  let selected_option = $derived.by(() => {
    if (!Array.isArray(options) || options.length === 0) return null;
    if (multiple) {
      const selected_values = Array.isArray(value) ? value : [];
      return options.filter((option) => selected_values.includes(option.value));
    }
    return options.find((option) => option.value === value) ?? null;
  });

  // Check if an option is selected (works for both single and multiple)
  function is_option_selected(option_value) {
    if (multiple) {
      return Array.isArray(value) ? value.includes(option_value) : false;
    }
    return value === option_value;
  }

  function handle_select(option, event = null) {
    const new_value = option?.value ?? null;

    if (multiple) {
      const current_values = Array.isArray(value) ? [...value] : [];
      
      if (new_value === null || new_value === '') {
        // Clear all selections
        if (allow_null) {
          value = [];
          on_select?.({ ...option, value: [] }, event);
        }
        return;
      }

      // Toggle selection: remove if already selected, add if not
      if (current_values.includes(new_value)) {
        value = current_values.filter(v => v !== new_value);
      } else {
        value = [...current_values, new_value];
      }
      
      on_select?.({ ...option, value }, event);
    } else {
      // Single selection mode
      if (!allow_null && new_value === null && value !== null) {
        return;
      }

      // deselect if the same option is selected again
      if (value === new_value && allow_null) {
        value = null;
        on_select?.({ ...option, value: null }, event);
        return;
      }

      value = new_value;
      on_select?.({ ...option, value: new_value }, event);
    }

    // ATTENTION: We must not hide the popover here, otherwise the parent element receives click events
    // e.g. creating an accidental booking. See: https://github.com/michael/system606/issues/1231
    // However, the popover closes anyways eventually once the form is successfully submitted (see AccountSwitcher.svelte)
    // if (container === 'dropdown') {
    //   popover_ref?.hidePopover();
    // }

    // Don't clear search term here - will be handled by the blur event
    // or after selection is complete
    // search_term = '';
  }

  function clear_selection(event) {
    event.stopPropagation();
    if (allow_null) {
      handle_select({ value: null }, event);
    }
  }

  function keyboard_navigate_option_list(event) {
    // Filter out undefined options *before* checking disabled status
    const valid_filtered_options = filtered_options.filter(Boolean);
    const selectable_options = valid_filtered_options.filter(
      (option) => !option.disabled
    );
    const options_length = selectable_options.length;

    function navigate_option(direction) {
      event.preventDefault();
      focused_index =
        focused_index + direction < 0
          ? options_length - 1
          : focused_index + direction >= options_length
            ? 0
            : focused_index + direction;
      option_refs[focused_index]?.scrollIntoView({ block: "nearest" });
    }

    switch (event.key) {
      case "ArrowDown":
        navigate_option(1);
        break;

      case "ArrowUp":
        navigate_option(-1);
        break;

      case "Tab":
        if (event.shiftKey) {
          navigate_option(-1);
        } else {
          navigate_option(1);
        }
        break;

      case "Enter":
        event.preventDefault();
        if (focused_index >= 0) {
          handle_select(selectable_options[focused_index], event);
        }
        break;

      case "Escape":
        popover_ref?.hidePopover();
        search_term = "";
        on_search?.("");
        break;

      default:
        break;
    }
  }

  // Focus management
  $effect(() => {
    // Reset focused_index if search term changes or results update
    if (!search_term?.length) {
      focused_index = -1;
      return;
    }

    // Set initial focus to the first item if there are results
    const has_valid_focus = filtered_options[focused_index] !== undefined;
    if (!has_valid_focus && filtered_options.length > 0) {
      focused_index = 0;
    }
  });

  $effect(() => {
    if (container === "dropdown" && popover_is_open && dropdown_ref && anchor) {
      const unregister = register_click_outside_element({
        el: dropdown_ref,
        anchor,
        on_click_outside: () => {
          popover_ref?.hidePopover();
          // ? Is this line necessary:
          document.activeElement?.blur();
        },
      });

      return () => {
        unregister();
      };
    }
  });
</script>

<div
  class="search-select flex-column relative {container}"
  style:--option-size={{ small: "42px", medium: "48px" }[option_size]}
  style:--search-select-width={width_value}
  bind:this={dropdown_ref}
>
  <!-- For form submission and validation -->
  {#if multiple && Array.isArray(value)}
    {#each value as val, index}
      <input type="text" name="{name}[]" value={val} {required} class="visually-hidden" tabindex="-1" aria-hidden="true" />
    {/each}
  {:else}
    <input type="text" {name} value={value || ""} {required} class="visually-hidden" tabindex="-1" aria-hidden="true" />
  {/if}

  {#if enable_search || container === "dropdown"}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="search-select-input-wrapper items-center"
      class:border-1-system={container === "dropdown" && !has_borderless_trigger_variant}
      class:selected-outline-if-has-child-in-focus={container === "dropdown" && !has_borderless_trigger_variant}
      class:borderless-trigger={container === "dropdown" && has_borderless_trigger_variant}
      class:has-selected-option={multiple ? (Array.isArray(value) && value.length > 0) : value}
      class:disabled
      bind:this={anchor}
      onclick={() => {
        if (
          open_on_focus &&
          container === "dropdown" &&
          !popover_is_open &&
          !disabled
        ) {
          popover_ref.showPopover();
        }
      }}
    >
      <!-- Start icon (Search) or Image -->
      {#if start_icon && typeof start_icon === "function"}
        {@render start_icon()}
      {:else if start_icon && typeof start_icon === "string"}
        <div class="icon-wrapper flex-row items-center justify-center">
          <Icon name={start_icon} />
        </div>
      {:else if show_search_icon && !disabled && (container === "inline" || (multiple ? true : !selected_option?.image) || search_input_is_focused || popover_is_open)}
        <!-- Search icon -->
        <label
          class="search-icon flex-row items-center justify-center"
          for={search_input_id}
          title="Search"
        >
          <ButtonLabel
            align="start"
            start_icon={container === "dropdown" &&
            !multiple &&
            !selected_option?.image 
            && options.some((o) => o.image)
            && selected_option
              ? "image"
              : // : allow_null && value
                //   ? 'check-inverted'
                //   : 'radio-true'
                "search"}
            variant="brand ghostly"
          />
        </label>
      {:else if !multiple && value && selected_option?.image}
        <Image
          {...selected_option.image}
          display_width={{ small: 42 - 4, medium: 48 - 4 }[option_size]}
          display_height={{ small: 42 - 4, medium: 48 - 4 }[option_size]}
          aspect_ratio={1}
        />
      {/if}

      <!-- Search input -->
      <input
        id={search_input_id}
        type="search"
        bind:value={search_term}
        bind:this={search_input_ref}
        placeholder={container === "dropdown"
          ? (multiple 
              ? (Array.isArray(value) && value.length > 0 
                  ? (Array.isArray(selected_option) && selected_option.length === 1 
                      ? selected_option[0].title 
                      : Array.isArray(selected_option) && selected_option.length > 1
                        ? t('count_selected', { count: selected_option.length })
                        : placeholder)
                  : placeholder)
              : selected_option?.input_title || selected_option?.title || placeholder)
          : placeholder}
        class="search-select-input ghostly text-control truncate"
        aria-controls="options-list"
        aria-autocomplete="list"
        autocomplete="off"
        disabled={disabled || !enable_search}
        onfocus={(event) => {
          search_input_is_focused = true;
          if (container === "dropdown" && !popover_is_open && !disabled) {
            popover_ref.showPopover();
          }
        }}
        onblur={(event) => {
          search_input_is_focused = false;
          if (!selecting_in_progress) {
            search_term = "";
          }
        }}
        onkeydown={(event) => {
          event.stopPropagation();
          if (
            container === "dropdown" &&
            event.key === "ArrowDown" &&
            !popover_is_open
          ) {
            popover_ref.showPopover();
          }
          keyboard_navigate_option_list(event);
        }}
      />

      <!-- Clear button -->
      {#if (allow_null && ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value)) && !search_input_is_focused) || (search_term && search_input_is_focused)}
        <button
          type="button"
          class="clear-button ms-3"
          onclick={(event) => {
            if (search_input_is_focused && search_term) {
              // Clear search term when input is focused
              search_term = "";
            } else if (allow_null && ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value))) {
              // Clear selection when input is not focused
              clear_selection(event);
            }
          }}
          title={search_input_is_focused && search_term ? t("clear") : t("clear_selection")}
        >
          <ButtonLabel 
            start_icon={search_input_is_focused && search_term ? "clear" : "close"} 
            variant="brand ghostly" 
          />
        </button>
      {/if}
      {#if end_icon}
        {@render end_icon()}
      {:else if container === "dropdown" && !disabled && (!allow_null || (multiple ? (!Array.isArray(value) || value.length === 0) : (!value && allow_null)))}
        <!-- Important type="button" to prevent form submission in a parent form -->
        <button
          type="button"
          tabindex="-1"
          onclick={() => {
            if (popover_is_open) {
              popover_ref.hidePopover();
            } else if (search_input_ref && enable_search) {
              search_input_ref.focus();
            }
          }}
        >
          <ButtonLabel start_icon="small-arrow-down" variant="brand ghostly" />
        </button>
      {/if}
    </div>
  {/if}

  {#snippet options_list()}
    {#if filtered_options.length > 0 || search_term?.length > 0}
      <!-- svelte-ignore a11y_interactive_supports_focus -->
      <div
        class="select-list flex-column auto-scroll-y"
        role="listbox"
        aria-label="Options"
        onkeydown={keyboard_navigate_option_list}
        style="width: {width_value}"
        style:--option-size={{ small: "42px", medium: "48px" }[option_size]}
      >
        <!-- <ScrollIndicator> -->
          {#each filtered_options as option, index (option.value)}
            <div 
              class="select-list-option-container flex-row items-center gap-x g-2"
              class:selected={is_option_selected(option.value)}
              class:focused={index === focused_index}
            >
              <button
                in:fade={{duration: 50}}
                type="button"
                class="select-list-option border-xs items-center full-width"
                style:--preview-size='{{ small: 40, medium: 48 }[option_size]}px'
                disabled={option.disabled}
                onpointerdown={(event) => {
                  event.preventDefault(); // Prevent input blur during selection
                  selecting_in_progress = true;
                }}
                onpointerup={(event) => {
                  handle_select(option, event);
                  selecting_in_progress = false;
                  search_term = "";
                }}
                role="option"
                aria-selected={is_option_selected(option.value)}
                bind:this={option_refs[index]}
                data-option-value={option.value}
                {...((option.icon_color1 || option.icon_color2) && {
                  style: [
                    option.icon_color1 && `--icon-color1: ${option.icon_color1}`,
                    option.icon_color2 && `--icon-color2: ${option.icon_color2}`
                  ].filter(Boolean).join('; ')
                })}
              >
              {#if option_content_snippet}
                {@render option_content_snippet(option, { selected: is_option_selected(option.value), focused: index === focused_index })}
              {:else}
              
                <!-- Visual -->
                <div class="select-visual flex-row items-center justify-center">
                  <!-- Selected icon -->
                  {#if is_option_selected(option.value)}
                    <div class="selected-icon flex-row items-center justify-center">
                      <Icon
                        name={allow_null ? "check-inverted" : "radio-true"}
                      />
                    </div>
                  {/if}
                  {#if option.image}
                    <Image
                      class="select-image"
                      {...option.image}
                      display_width={{ small: 40, medium: 48 }[option_size]}
                      display_height={{ small: 40, medium: 48 }[option_size]}
                    />
                  {:else if option.icon}
                    <div
                      class="select-image flex-row items-center justify-center"
                    >
                      <Icon name={option.icon} />
                    </div>
                  {/if}
                </div>

                <!-- Title and description -->
                <div
                  class="title-and-description flex-column full-width gap-y g-3"
                  title={`${option.title}${option.description ? `\n${option.description}` : ""}`}
                >
                  <div class="item-title leading-trim text-control truncate">
                    {option.title ?? option.value}
                  </div>
                  {#if option.description}
                    <div class="item-description leading-trim text-caption truncate">
                      {option.description}
                    </div>
                  {/if}
                </div>
              {/if}
              </button>
              
              {#if option_tools_snippet}
                <div class="select-list-option-tools flex-row items-center">
                  {@render option_tools_snippet(option, { selected: is_option_selected(option.value), focused: index === focused_index })}
                </div>
              {/if}
            </div>
          {/each}

          {#if show_searching_indicator}
            <div in:fade={{duration: 150}} class="search-status text-control text-center text-3 px py p-3" role="status">
              {t("searching")}...
            </div>
          {:else if !awaiting_search_results && filtered_options.length === 0 && search_term?.length >= min_search_term_length}
            <div in:fade={{delay: 50, duration: 150}} class="search-status text-control text-center text-3 px py p-3" role="status">
              {t("no_results_found")}
            </div>
          {:else if !awaiting_search_results && options.length === 0 && search_term?.length < min_search_term_length}
            <div in:fade={{delay: 50, duration: 150}} class="search-status text-control text-center text-3 px py p-3" role="status">
              {t("start_typing_to_search")}
            </div>
          {/if}
        <!-- </ScrollIndicator> -->
      </div>
    {/if}
  {/snippet}

  {#if container === "inline"}
    {@render options_list()}
  {:else if container === "dropdown"}
    <Popover
      bind:ref={popover_ref}
      bind:is_open={popover_is_open}
      {placement}
      {anchor}
      id={popover_id}
      offset={1}
      mode="manual"
      class='dropdown'
      min_height={192}
      min_width={200}
      on_toggle={(is_open) => {
        if (!is_open) {
          // Reset search term when popover closes
          search_term = "";
          search_input_ref.blur();
          on_blur?.();
        }
      }}
    >
      {@render options_list(popover_ref)}
    </Popover>
  {/if}
</div>
{#if error}
  <p class='text-control text-error'>{error}</p>
{/if}

<style lang="postcss">
  .search-select {
    width: var(--search-select-width, fit-content);
    &.inline {
      height: 100%;
    }
    &.dropdown {
      height: fit-content;
    }
    /* Error state validation styling */
    &:has(input:user-invalid):not(:has(.search-select-input:focus-within)) {
      :global(.border-1-system):after {
        --color-stroke: var(--color-destructive) !important;
      }
      .search-select-input-wrapper {
        background: 
          /* Little warning circle */
          url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23dc3545' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='12'/%3E%3C/svg%3E") calc(100% - var(--input-inline-spacing) - 40px) center / 10px no-repeat,
          /* Light background */
          oklch(from var(--color-destructive) l c h / 0.14);
        .search-select-input {
        }
      }
    }
  }
  .select-list-option-container {
    /* Highlight with a tint of the current foreground so text/icons stay
       readable on both light and dark surfaces (no emphasis fg/bg flip). */
    @media (hover: hover) {
      &:hover {
        background-color: color-mix(
          in oklch,
          var(--color-foreground, var(--color-default-foreground)) 10%,
          transparent
        );
      }
    }

    &.focused,
    &.selected {
      background-color: color-mix(
        in oklch,
        var(--color-foreground, var(--color-default-foreground)) 14%,
        transparent
      );
    }
  }
  .search-select-input-wrapper,
  .select-list-option {
    --list-spacing: 3px;
  }
  @media (hover: hover) {
    .select-list-option-tools {
      opacity: 0;
    }
    .select-list-option-container:hover .select-list-option-tools {
      opacity: 1;
    }
  }
  .select-list {
    height: 100%;
    max-width: 100%;
    /* The popover can be wider than the width prop (flush placement matches
       the anchor) — the rows must fill it, or a white strip shows at the end. */
    min-width: 100%;
  }
  .select-list :global(.scroll-indicator) {
    scrollbar-width: thin;
    overflow-x: hidden; /* there's a one pixel overflow that causes a horizontal scrollbar to appear. TODO: investigate the cause and remove this temporary fix */
  }
  .select-list:has(:global(.select-image)) :global(.select-visual) {
    margin-inline-end: var(--size-3);
  }
  /* Translucent plate only behind real image thumbnails (.media-item) —
     on icon-only options it painted a stray square over the row highlight. */
  .select-list :global(.select-visual:has(.media-item)) {
    background-color: oklch(from var(--color-default-background) l c h / 0.2);
  }
  /* if there's any child with a select-visual, make the select-list-option display: flex */
  /* :global(.select-list:has(.select-list-option > .select-visual > *) .select-list-option .select-visual) {
    display: flex;
  } */
  .select-list-option {
    --option-padding: var(--size-2);
    padding: var(--option-padding);
    /* box-shadow: inset 0 0 0 calc(var(--option-padding) - 1px) var(--color-background, var(--color-default-background)); */
    border-bottom-width: 0;
    display: grid;
    grid-template-columns: auto 1fr;
    position: relative;
    min-height: calc(var(--option-size) + 2 * var(--list-spacing) + var(--option-padding));
    :global(.select-visual) {
      /* hide the select-visual by default, only show it if there's at least one child with a select-visual */
      /* display: none; */
      grid-column: 1 / 2;
      grid-row: 1 / 2;
      position: relative;
      margin-inline-start: 2px;
      margin-block: 1px;
      min-width: var(--preview-size);
      height: var(--preview-size);
      border-radius: calc(var(--radius-xs) - var(--option-padding) - 2px);
      :global(.select-image) {
        aspect-ratio: 1 / 1;
        width: var(--preview-size);
        height: var(--preview-size);
      }
      :global(.selected-icon) {
        border-radius: calc(var(--radius-xs) - var(--option-padding) - 2px);
        z-index: 1;
        width: fit-content;
        margin-inline: auto;
        --icon-color1: var(--color-foreground, var(--color-default-foreground));
        color: var(--color-foreground, var(--color-default-foreground));
      }    
      /* Overlay treatment ONLY for real image thumbnails: the radio floats on
         a light scrim over the picture. Icon options instead REPLACE the icon
         with the radio — stacking both read as broken. */
      &:has(:global(.media-item)) {
        :global(.selected-icon) {
          /* Light scrim so a bright radio stays visible over avatars without
             reading as a solid black mask on dark menu surfaces. */
          background-color: color-mix(
            in oklch,
            var(--color-background, var(--color-default-background)) 45%,
            transparent
          );
          position: absolute;
          height: var(--preview-size);
          width: var(--preview-size);
        }
      }
      &:has(:global(.selected-icon)):not(:has(:global(.media-item))) {
        :global(.select-image) {
          display: none;
        }
      }
    }
    .title-and-description {
      grid-column: 2 / 3;
      grid-row: 1 / 2;
    }
    .item-description {
      /* Prefer remapped foreground over --text-3 / --color-default-foreground-*,
         which can stay stuck on the canvas default inside themed menus. */
      color: var(--text-2);
    }
  }
  .search-select-input-wrapper {
    width: 100%;
    display: grid;
    grid-template-columns: calc(var(--option-size) + var(--list-spacing)) 1fr auto auto;
    border-radius: var(--input-border-radius);
    > * {
      min-height: calc(var(--option-size) + 2 * var(--list-spacing));
    }
    .icon-wrapper {
      width: var(--option-size);
      height: var(--option-size);
    }
    :global(.media-item) {
      margin-inline: auto !important;
      border-radius: var(--input-border-radius);
    }
    .search-select-input {
      padding-inline: 0;
      grid-column: 2 / 3;
      &:first-child {
        grid-column: 1 / 3;
        padding-inline-start: var(--input-inline-spacing);
      }
      &:disabled {
        pointer-events: none;
        user-select: none;
      }
      &::-webkit-search-cancel-button {
        appearance: none;
      }
    }
    &.disabled {
      opacity: 0.5;
    }
  }
  
  /* Dropdown variant */
  .dropdown :global(.popover-content) {
    padding: 0;
  }
  :global(.dropdown .select-list) {
    /* Opinionated max height for dropdowns */
    max-height: min(calc(100svh - var(--dynamic-island-bottom-edge) - 40px), 400px);
  }
  .dropdown .search-select-input-wrapper {
    &.has-selected-option .search-select-input {
      opacity: 1;
      &:not(:focus)::placeholder {
        color: var(--color-default-foreground);
      }
    }
    &.borderless-trigger {
      box-shadow: none;
      &:focus-within {
        background-color: var(--color-default-background-50);
      }
    }
  }
  .dropdown .search-select-input-wrapper :global(.media-item) {
    margin-inline-start: 2px;
  }
  
  /* Inline variant */
  .inline {
    .search-select-input {
      grid-column: 2 / 4;
    }
    .search-select-input-wrapper {
      border-radius: 0;
    }
  }
  .inline.search-select {
    gap: 1px;
    max-height: var(--search-select-max-height, 100%); /* make sure the scrollable area is only on the select-list and the search input stays in view */
    /* background-color: var(--color-background, var(--color-default-stroke));
    > * {
      background-color: var(--color-background, var(--color-default-background));
    } */
  }
</style>
